// Atom speaker volume, scaled to be comparable with BBC tone channels.
// The BBC volumeTable[0] (loudest) is 0.25 (1.0 / 4 channels).
const speakerVolume = 0.5;

const volumeTable = new Float32Array(16);
(() => {
    let f = 1.0;
    for (let i = 0; i < 15; ++i) {
        volumeTable[i] = f / 4; // Bakes in the per channel volume
        f *= Math.pow(10, -0.1);
    }
    volumeTable[15] = 0;
})();

function makeSineTable(attenuation) {
    const sineTable = new Float32Array(8192);
    for (let i = 0; i < sineTable.length; ++i) {
        sineTable[i] = Math.sin((2 * Math.PI * i) / sineTable.length) * attenuation;
    }
    return sineTable;
}

export class SoundChip {
    constructor(onBuffer) {
        this._onBuffer = onBuffer;
        // 4MHz input signal. Internal divide-by-8
        this.soundchipFreq = 4000000.0 / 8;
        const sampleRate = this.soundchipFreq;
        // Square wave changes every time a counter hits zero: A full wave needs to be 2x counter zeros.
        this.waveDecrementPerSecond = this.soundchipFreq / 2;
        // Each sample in the buffer represents (1/sampleRate) time, so each time
        // we generate a sample, we need to decrement the counters by this amount:
        this.sampleDecrement = this.waveDecrementPerSecond / sampleRate;
        // How many samples are generated per CPU cycle.
        this.samplesPerCycle = sampleRate / 2000000;
        this.minCyclesWELow = 14; // Somewhat empirically derived; Repton 2 has only 14 cycles between WE low and WE high (@0x2caa)

        this.registers = new Uint16Array(4);
        this.counter = new Float32Array(4);
        this.outputBit = [false, false, false, false];
        this.volume = new Float32Array(4);
        this.generators = [
            this.toneChannel.bind(this),
            this.toneChannel.bind(this),
            this.toneChannel.bind(this),
            this.noiseChannel.bind(this),
            this.sineChannel.bind(this),
        ];

        this.sineTable = makeSineTable(1 / this.generators.length);
        this.sineStep = 0;
        this.sineOn = false;
        this.sineTime = 0;

        this.lfsr = 0;
        this.shiftLfsr = this.shiftLfsrWhiteNoise.bind(this);

        this.enabled = true;
        this.scheduler = { epoch: 0 };
        this.lastRunEpoch = 0;
        this.activeTask = null;

        this.residual = 0;
        this.position = 0;
        this.buffer = new Float32Array(512);

        this.latchedRegister = 0;
        this.slowDataBus = 0;
        this.active = false;

        this.toneGenerator = {
            mute: () => {
                this.catchUp();
                this.sineOn = false;
            },
            tone: (freq) => {
                this.catchUp();
                this.sineOn = true;
                this.sineStep = (freq / sampleRate) * this.sineTable.length;
            },
        };
    }

    sineChannel(channel, out, offset, length) {
        if (!this.sineOn) return;

        for (let i = 0; i < length; ++i) {
            out[i + offset] += this.sineTable[this.sineTime & (this.sineTable.length - 1)];
            this.sineTime += this.sineStep;
        }
        while (this.sineTime > this.sineTable.length) this.sineTime -= this.sineTable.length;
    }

    _doChannelStep(channel, addAmount) {
        const newValue = this.counter[channel] - this.sampleDecrement;
        if (newValue < 0) {
            this.counter[channel] = Math.max(0, newValue + addAmount);
            this.outputBit[channel] = !this.outputBit[channel];
            return this.outputBit[channel];
        } else {
            this.counter[channel] = newValue;
            return false;
        }
    }

    toneChannel(channel, out, offset, length) {
        const reg = this.registers[channel] === 0 ? 1024 : this.registers[channel];
        const vol = this.volume[channel];
        for (let i = 0; i < length; ++i) {
            this._doChannelStep(channel, reg);
            out[i + offset] += this.outputBit[channel] * vol;
        }
    }

    shiftLfsrWhiteNoise() {
        const bit = (this.lfsr & 1) ^ ((this.lfsr & (1 << 1)) >>> 1);
        this.lfsr = (this.lfsr >>> 1) | (bit << 14);
    }

    shiftLfsrPeriodicNoise() {
        this.lfsr >>= 1;
        if (this.lfsr === 0) this.lfsr = 1 << 14;
    }

    noisePoked() {
        this.shiftLfsr =
            this.registers[3] & 4 ? this.shiftLfsrWhiteNoise.bind(this) : this.shiftLfsrPeriodicNoise.bind(this);
        this.lfsr = 1 << 14;
    }

    addFor(channel) {
        channel = channel | 0;
        switch (this.registers[channel] & 3) {
            case 0:
                return 0x10;
            case 1:
                return 0x20;
            case 2:
                return 0x40;
            case 3:
                return this.registers[channel - 1];
        }
    }

    noiseChannel(channel, out, offset, length) {
        const add = this.addFor(channel),
            vol = this.volume[channel];
        for (let i = 0; i < length; ++i) {
            if (this._doChannelStep(channel, add)) this.shiftLfsr();
            out[i + offset] += (this.lfsr & 1) * vol;
        }
    }

    debugPokeAll(c0, v0, c1, v1, c2, v2, c3, v3) {
        this.catchUp();
        this.registers[0] = c0 & 0xffffff;
        this.registers[1] = c1 & 0xffffff;
        this.registers[2] = c2 & 0xffffff;
        this.registers[3] = c3 & 0xffffff;
        this.volume[0] = volumeTable[v0];
        this.volume[1] = volumeTable[v1];
        this.volume[2] = volumeTable[v2];
        this.volume[3] = volumeTable[v3];
        this.noisePoked();
    }

    generate(out, offset, length) {
        offset = offset | 0;
        length = length | 0;
        for (let i = 0; i < length; ++i) {
            out[i + offset] = 0.0;
        }
        if (!this.enabled) return;
        for (let i = 0; i < this.generators.length; ++i) {
            this.generators[i](i, out, offset, length);
        }
    }

    catchUp() {
        const cyclesPending = this.scheduler.epoch - this.lastRunEpoch;
        if (cyclesPending > 0) this.advance(cyclesPending);
        this.lastRunEpoch = this.scheduler.epoch;
    }

    setScheduler(scheduler_) {
        this.scheduler = scheduler_;
        this.lastRunEpoch = this.scheduler.epoch;
        this.activeTask = this.scheduler.newTask(() => {
            if (this.active) this.poke(this.slowDataBus);
        });
    }

    render(out, offset, length) {
        this.catchUp();
        const fromBuffer = this.position > length ? length : this.position;
        for (let i = 0; i < fromBuffer; ++i) {
            out[offset + i] = this.buffer[i];
        }
        offset += fromBuffer;
        length -= fromBuffer;
        for (let i = fromBuffer; i < this.position; ++i) {
            this.buffer[i - fromBuffer] = this.buffer[i];
        }
        this.position -= fromBuffer;
        if (length !== 0) {
            this.generate(out, offset, length);
        }
    }

    advance(cycles) {
        const num = cycles * this.samplesPerCycle + this.residual;
        let rounded = num | 0;
        this.residual = num - rounded;
        const bufferLength = this.buffer.length;
        while (rounded > 0) {
            const leftInBuffer = bufferLength - this.position;
            const numSamplesToGenerate = Math.min(rounded, leftInBuffer);
            this.generate(this.buffer, this.position, numSamplesToGenerate);
            this.position += numSamplesToGenerate;
            rounded -= numSamplesToGenerate;

            if (this.position === bufferLength) {
                this._onBuffer(this.buffer);
                this.buffer = new Float32Array(bufferLength);
                this.position = 0;
            }
        }
    }

    poke(value) {
        this.catchUp();

        let command;
        if (value & 0x80) {
            this.latchedRegister = value & 0x70;
            command = value & 0xf0;
        } else {
            command = this.latchedRegister;
        }
        const channel = (command >> 5) & 0x03;

        if (command & 0x10) {
            // Volume setting
            const newVolume = value & 0x0f;
            this.volume[channel] = volumeTable[newVolume];
        } else if (channel === 3) {
            // For noise channel we always update the bottom bits.
            this.registers[channel] = value & 0x0f;
            this.noisePoked();
        } else if (command & 0x80) {
            // Low period bits.
            this.registers[channel] = (this.registers[channel] & ~0x0f) | (value & 0x0f);
        } else {
            // High period bits.
            this.registers[channel] = (this.registers[channel] & 0x0f) | ((value & 0x3f) << 4);
        }
    }

    updateSlowDataBus(slowDataBus, active) {
        this.slowDataBus = slowDataBus;
        this.active = active;
        // TODO: this probably isn't modeled correctly. Currently the sound chip "notices" a new data bus value some
        // fixed number of cycles after WE (write enable) is triggered. In reality, the sound chip likely pulls data off
        // the bus at a fixed point in its cycle, iff WE is active.
        if (active) {
            this.activeTask.ensureScheduled(true, this.minCyclesWELow);
        }
    }

    snapshotState() {
        return {
            registers: this.registers.slice(),
            counter: this.counter.slice(),
            outputBit: [...this.outputBit],
            volume: this.volume.slice(),
            lfsr: this.lfsr,
            latchedRegister: this.latchedRegister,
            residual: this.residual,
            sineOn: this.sineOn,
            sineStep: this.sineStep,
            sineTime: this.sineTime,
        };
    }

    restoreState(state) {
        this.registers.set(state.registers);
        this.counter.set(state.counter);
        this.outputBit[0] = state.outputBit[0];
        this.outputBit[1] = state.outputBit[1];
        this.outputBit[2] = state.outputBit[2];
        this.outputBit[3] = state.outputBit[3];
        this.volume.set(state.volume);
        this.lfsr = state.lfsr;
        this.latchedRegister = state.latchedRegister;
        // Sync to current scheduler epoch to avoid a catch-up burst
        this.lastRunEpoch = this.scheduler.epoch;
        this.residual = state.residual;
        this.sineOn = state.sineOn;
        this.sineStep = state.sineStep;
        this.sineTime = state.sineTime;
        // Rebind the LFSR function based on noise register
        this.shiftLfsr =
            this.registers[3] & 4 ? this.shiftLfsrWhiteNoise.bind(this) : this.shiftLfsrPeriodicNoise.bind(this);
        // Reset output buffer
        this.position = 0;
        this.buffer.fill(0);
    }

    reset(hard) {
        if (!hard) return;
        for (let i = 0; i < 4; ++i) {
            this.counter[i] = 0;
            this.registers[i] = 0;
            // Real hardware would be volumeTable[0] but that's really quite loud and surprising...
            this.volume[i] = volumeTable[8];
        }
        this.noisePoked();
        this.lastRunEpoch = this.scheduler.epoch;
    }

    enable(e) {
        this.enabled = e;
    }

    mute() {
        this.enabled = false;
    }

    unmute() {
        this.enabled = true;
    }
}

/**
 * AtomSoundChip -- Acorn Atom sound via a 1-bit speaker driven by the PPIA.
 * Uses only the sine channel (shared with BBC for tape tones) and a speaker
 * channel with DC-blocking filter.
 */
export class AtomSoundChip extends SoundChip {
    constructor(onBuffer, { cpuSpeed = 1000000 } = {}) {
        super(onBuffer);
        this.samplesPerCycle = this.soundchipFreq / cpuSpeed;
        this.secondsPerCycle = 1 / cpuSpeed;

        // Replace the BBC tone/noise generators with just sine + speaker.
        this.generators = [this.sineChannel.bind(this), this.speakerChannel.bind(this)];
        // Recompute sine attenuation for the Atom's 2-channel mix
        // (parent computed it for 5 BBC channels).
        this.sineTable = makeSineTable(1 / this.generators.length);

        this.speakerGenerator = {
            mute: () => {
                this.catchUp();
                this.speakerReset();
            },
            pushBit: (bit, cycles, seconds) => {
                this.catchUp();
                this.updateSpeaker(bit, cycles, seconds);
            },
        };

        this.bitChange = [];
        this.currentSpeakerBit = 0.0;
        this._speakerPrevIn = 0;
        this._speakerPrevOut = 0;
    }

    reset(hard) {
        super.reset(hard);
        if (hard) this.speakerReset();
    }

    speakerReset() {
        this.bitChange = [];
        this.currentSpeakerBit = 0.0;
        this._speakerPrevIn = 0;
        this._speakerPrevOut = 0;
    }

    speakerChannel(channel, out, offset, length) {
        let fromTime = this.scheduler.epoch - length;
        let bitIndex = 0;
        // DC-blocking high-pass filter: y[n] = x[n] - x[n-1] + alpha * y[n-1]
        // The SoundChip runs at 500 kHz (4 MHz / 8). For a ~20 Hz cutoff:
        // alpha = 1 - 2*pi*fc/fs = 1 - 2*pi*20/500000 ≈ 0.99975
        const alpha = 0.99975;

        for (let i = 0; i < length; ++i) {
            while (bitIndex < this.bitChange.length && this.bitChange[bitIndex].cycles <= fromTime + i) {
                this.currentSpeakerBit = this.bitChange[bitIndex].bit;
                bitIndex++;
            }
            const input = this.currentSpeakerBit * speakerVolume;
            this._speakerPrevOut = input - this._speakerPrevIn + alpha * this._speakerPrevOut;
            this._speakerPrevIn = input;
            out[i + offset] += this._speakerPrevOut;
        }

        if (bitIndex > 0) {
            this.bitChange.splice(0, bitIndex);
        }
    }

    updateSpeaker(value, microCycle, seconds) {
        const cycles = microCycle + seconds / this.secondsPerCycle;
        this.bitChange.push({ bit: value ? 1.0 : 0.0, cycles });
    }
}

/**
 * InstrumentedSoundChip - wraps a real SoundChip and captures all writes
 * for debugging purposes. Provides the same interface as SoundChip.
 */
export class InstrumentedSoundChip extends SoundChip {
    constructor() {
        super(() => {}); // no audio output callback needed in headless mode
        this._capturedWrites = [];
        this._capturing = false;
        this._totalCycles = 0;
    }

    poke(value) {
        if (this._capturing) {
            this._capturedWrites.push({ cycle: this._totalCycles, value });
        }
        super.poke(value);
    }

    /** Start capturing SN76489 writes. Clears any previous capture. */
    startCapture() {
        this._capturedWrites = [];
        this._capturing = true;
    }

    /** Stop capturing and return the captured writes. */
    stopCapture() {
        this._capturing = false;
        return this._capturedWrites;
    }

    /** Get the captured writes without stopping. */
    getCapturedWrites() {
        return this._capturedWrites;
    }

    /** Clear captured writes. */
    clearCapture() {
        this._capturedWrites = [];
    }

    /** Track total cycles for timestamps. Called by the scheduler. */
    advance(cycles) {
        this._totalCycles += cycles;
        super.advance(cycles);
    }

    /** Read current SN76489 register state in a friendly format. */
    getState() {
        return {
            tone: [this.registers[0], this.registers[1], this.registers[2]],
            noise: this.registers[3],
            volume: [
                this._attenuationFromVolume(0),
                this._attenuationFromVolume(1),
                this._attenuationFromVolume(2),
                this._attenuationFromVolume(3),
            ],
            lfsr: this.lfsr,
            latchedRegister: this.latchedRegister,
        };
    }

    /** Convert internal float volume back to 0-15 attenuation. */
    _attenuationFromVolume(channel) {
        const v = this.volume[channel];
        // volumeTable[15] = 0 (silent), volumeTable[0] = loudest
        for (let i = 0; i < 16; i++) {
            if (Math.abs(volumeTable[i] - v) < 0.001) return i;
        }
        return 15; // silent
    }
}

export class FakeSoundChip {
    reset() {}

    enable() {}

    catchUp() {}

    mute() {}

    unmute() {}

    render() {}

    updateSlowDataBus() {}

    setScheduler() {}

    constructor() {
        this.toneGenerator = {
            mute: () => {},
            tone: () => {},
        };
        this.speakerGenerator = {
            mute: () => {},
            pushBit: () => {},
        };
    }

    snapshotState() {
        return {};
    }

    restoreState() {}
}
